import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Typography,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Snackbar,
  Alert,
  FormLabel,
  DialogActions,
  DialogTitle,
  DialogContent,
  Dialog,
} from "@mui/material";
import * as XLSX from "xlsx";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ServiceProviderCard from "../../components/ServiceProviderCard";
import ServiceProviderDrawer from "../../components/ServiceProviderDrawer";
import AssignServiceDialog from "../../components/AssignmentServiceDialog";

import axios from "axios";
import { useUser } from "../../provider/AuthProvider";
import UserAddDialog from "../../components/UserAddDialog";
import ConfirmDialog from "../../components/ComfirmationDialog";
import {
  Cancel,
  CheckCircle,
  CloudDownload,
  CloudUpload,
  Delete,
  SendSharp,
} from "@mui/icons-material";
import CSVImportDialog from "../../components/CSVImportDialog";

const ServiceProviderPage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [targetProvider, setTargetProvider] = useState(null);
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProvider, setDeleteProvider] = useState(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sending, setSending] = useState(false);
  const openDrawer = (provider) => {
    setSelectedProvider(provider);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const openAssignDialog = (provider) => {
    setTargetProvider(provider);
    setAssignDialogOpen(true);
  };

  const handleProviderUpdate = (updatedProvider) => {
    setProviders((prev) =>
      prev.map((provider) =>
        provider.id === updatedProvider.id ? updatedProvider : provider
      )
    );
    setSelectedProvider(updatedProvider);
  };

  const handleBulkStatusChange = async (status) => {
    setIsLoading(true);
    try {
      console.log(selectedUserIds);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/userBulkActive`,
        {
          userIds: selectedUserIds, // <-- pass array of selected user IDs
          status,
        }
      );

      setSnack({
        open: true,
        message: t("sp_page.bulk_status_updated", { count: selectedUserIds.length, status }),
        severity: "success",
      });
    } catch (error) {
      console.error("Bulk status change error:", error);
      setSnack({
        open: true,
        message: t("sp_page.bulk_status_failed"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      fetchUsers(); // Refresh user list
    }
  };

  const handleBulkDelete = async () => {
    setIsLoading(true);
    try {
      console.log(selectedUserIds);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/userBulkDelete`,
        {
          userIds: selectedUserIds,
        }
      );

      setSnack({
        open: true,
        message: t("sp_page.bulk_deleted", { count: selectedUserIds.length }),
        severity: "success",
      });
    } catch (error) {
      console.error("Bulk status change error:", error);
      setSnack({
        open: true,
        message: t("sp_page.bulk_delete_failed"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      fetchUsers(); // Refresh user list
    }
  };

  const handleSendNotify = async () => {
    if (!messageTitle.trim() || !messageBody.trim()) {
      setSnack({
        open: true,
        message: t("sp_page.title_and_body_required"),
        severity: "warning",
      });
      return;
    }

    if (!selectedUserIds.length) {
      setSnack({
        open: true,
        message: t("sp_page.select_at_least_one"),
        severity: "warning",
      });
      return;
    }

    try {
      setSending(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/create`,
        {
          title: messageTitle,
          message: messageBody,
          type: "GENERAL",
          send_type: "immediate",
          schedule_at: null,
          to_user_ids: selectedUserIds,
          from_user_id: user.id,
        }
      );

      const results = res.data.results || [];

      const successCount = results.reduce(
        (sum, r) => sum + (r.successCount || 0),
        0
      );
      const failureCount = results.reduce(
        (sum, r) => sum + (r.failureCount || 0),
        0
      );
      const failedUsers = results
        .filter((r) => r.failureCount > 0)
        .map((r) => r.userId);

      setSnack({
        open: true,
        message:
          failureCount === 0
            ? `✅ Notifications sent to ${successCount} user(s).`
            : `⚠️ Sent to ${successCount}, failed for ${failureCount}: ${failedUsers.join(
                ", "
              )}`,
        severity: failureCount === 0 ? "success" : "warning",
      });
    } catch (error) {
      console.error("Notification send error:", error);
      setSnack({
        open: true,
        message: "❌ Failed to send notification. Try again.",
        severity: "error",
      });
    } finally {
      setSending(false);
      setOpenNotifyDialog(false);
      setMessageTitle("");
      setMessageBody("");
    }
  };

  const handleDownloadTemplate = () => {
    const headersAndExamples = [
      ["username", "email", "phone", "password", "role"], // header
      ["john_doe", "john@example.com", "+1234567890", "Password123!", "TENANT"],
      [
        "jane_land",
        "jane@landlord.com",
        "+0987654321",
        "SecurePass456!",
        "LANDLORD",
      ],
      [
        "plumb_master",
        "plumb@example.com",
        "+1122334455",
        "PipeFix789!",
        "SERVICE_PROVIDER",
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(headersAndExamples);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    XLSX.writeFile(wb, "user_template.csv");
  };

  const handleAddUser = async (newUser) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/createUser`,
        {
          ...newUser,
        }
      );
      setSnack({
        open: true,
        message: t("sp_page.user_created"),
        severity: "success",
      });
      setAddUserOpen(false);
      fetchUsers();
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: err.response?.data?.error || t("sp_page.user_create_failed"),
        severity: "error",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/getProviders/${user.id}`
      );
      setProviders(res.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setIsLoading(false);
    }
  };

  // const fetchAvailableServiceType = async () => {
  //   try {
  //     const res = await axios.get(
  //       `${process.env.REACT_APP_BACKEND_URL}/api/cmt/availableServiceTypeList/${user.id}`
  //     );
  //     setAvailableServiceTypes(res.data);
  //   } catch (error) {
  //     console.error("Failed to fetch service types:", error);
  //   }
  // };

  useEffect(() => {
    fetchUsers();
    // fetchAvailableServiceType();
  }, []);

  const filteredProviders = providers.filter((p) => {
    return (
      (p.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.phone.includes(searchTerm)) &&
      (serviceType ? p.serviceType === serviceType : true)
    );
  });

  const paginatedProviders = filteredProviders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage);
  const handleDelete = (provider) => {
    setDeleteProvider(provider);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteProvider) {
      try {
        setIsDeleting(true);
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/deleteUser/${deleteProvider.id}`
        );
        setSnack({ open: true, message: t("sp_page.user_deleted"), severity: "success" });
        fetchUsers();
      } catch (error) {
        setSnack({ open: true, message: t("sp_page.delete_failed"), severity: "error" });
      } finally {
        setDeleteDialogOpen(false);
        setDeleteProvider(null);
        setIsDeleting(false);
      }
    }
  };

  const sendResetPassword = async (provider) => {
    try {
      const res = await fetch(`/api/providers/${provider.id}/reset-password`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to send reset");
      alert("Reset password link sent");
    } catch (err) {
      console.error(err);
      alert("Error sending reset password");
    }
  };

  const setFlagged = async (p) => {
    console.log(p);
    const provider_id = p.id;
    setIsSubmit(true);
    const flagged = p.isFlagged;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/setFlaggedProvider`,
        {
          id: user.id,
          providerId: provider_id,
          flag: flagged,
        }
      );
      setProviders((prev) =>
        prev.map((p) =>
          p.id === provider_id ? { ...p, isFlagged: !flagged } : p
        )
      );
    } catch (e) {
      setSnack({
        open: true,
        message: t("sp_page.flag_update_failed"),
        severity: "error",
      });
    } finally {
      setIsSubmit(false);
    }
  };
  return (
    <Box className="main-container mt-4" ml={10}>
      <Grid container spacing={2} columns={{ xs: 2, sm: 8, md: 10 }}>
        <Grid item size={{ xs: 2, sm: 2, md: 2 }}>
          <Paper
            elevation={4}
            sx={{
              p: 2,
              height: "75vh",
              overflowY: "auto",
            }}
          >
            <FormControl
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                // width: "",
                display: "flex",
                p: 1,
                m: 1, // padding inside border
              }}
            >
              <FormLabel component="legend">{t("sp_page.csv_management")}</FormLabel>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ mt: 0.5 }}
                onClick={() => setCsvDialogOpen(true)}
              >
                {t("sp_page.import")}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CloudDownload />}
                sx={{ mt: 0.5 }}
                onClick={handleDownloadTemplate}
              >
                {t("sp_page.download_template")}
              </Button>
            </FormControl>
            <FormControl
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                // width: "",
                display: "flex",
                p: 1,
                m: 1, // padding inside border
              }}
            >
              <FormLabel component="legend">{t("sp_page.bulk_action")}</FormLabel>

              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleBulkStatusChange("active")}
                sx={{ color: "#fff", mt: 1 }}
                disabled={selectedUserIds.length == 0 ? true : false}
              >
                {t("sp_page.active")}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => handleBulkStatusChange("inactive")}
                sx={{ color: "#fff", mt: 1 }}
                disabled={selectedUserIds.length == 0 ? true : false}
              >
                {t("sp_page.inactive")}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => handleBulkDelete()}
                sx={{ color: "#fff", mt: 3 }}
                disabled={selectedUserIds.length == 0 ? true : false}
              >
                {t("sp_page.delete")}
              </Button>
              {/* <Button
                variant="outlined"
                startIcon={<SendSharp />}
                sx={{ mt: 3 }}
                disabled={selectedUserIds.length == 0 ? true : false}
                onClick={() => setOpenNotifyDialog(true)}
              >
                Send Message
              </Button> */}
              <Typography>{t("sp_page.selected_count", { count: selectedUserIds.length })}</Typography>
            </FormControl>
          </Paper>
        </Grid>
        <Grid item size={{ xs: 2, sm: 5, md: 6 }}>
          <Paper
            elevation={4}
            sx={{
              padding: 2,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid
              container
              spacing={4}
              columns={{ xs: 2, sm: 8, md: 10 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Grid item columns={{ xs: 2, sm: 4, md: 5 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t("sp_page.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item columns={{ xs: 2, sm: 2, md: 3 }}>
                <Button
                  color="info"
                  variant="contained"
                  onClick={() => setAddUserOpen(true)}
                  sx={{ color: "#fff" }}
                >
                  {t("sp_page.add")}
                </Button>
              </Grid>
            </Grid>
            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ p: 2, height: "50vh" }}
              >
                <CircularProgress size="50px" />
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  height: "67vh",
                  flexDirection: "column",
                  overflowY: "auto",
                }}
              >
                <Stack spacing={3}>
                  {paginatedProviders.map((provider) => {
                    const isSelected = selectedUserIds.includes(provider.id);
                    return (
                      <ServiceProviderCard
                        provider={provider}
                        selected={isSelected}
                        onClick={() => {
                          setSelectedUserIds(
                            (prev) =>
                              prev.includes(provider.id)
                                ? prev.filter((id) => id !== provider.id) // deselect
                                : [...prev, provider.id] // select
                          );
                        }}
                        onFlagged={(p) => setFlagged(p)}
                        onEdit={() => openDrawer(provider)}
                        onDelete={(p) => handleDelete(p)}
                        onResetPassword={(p) => sendResetPassword(p)}
                        // onAssginmentService={(p) => openAssignDialog(p)}
                      />
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Box mt={4} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
      {/* <Dialog
        open={openNotifyDialog}
        onClose={() => setOpenNotifyDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send Message</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Title"
              variant="outlined"
              sx={{ mb: 2 }}
              value={messageTitle}
              onChange={(e) => setMessageTitle(e.target.value)}
            />
            <TextField
              fullWidth
              label="Message Body"
              variant="outlined"
              multiline
              rows={4}
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNotifyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => handleSendNotify()}>
            {sending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog> */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
      <UserAddDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onAdd={handleAddUser}
        user_type={"SERVICE_PROVIDER"}
      />
      <CSVImportDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        onImported={() => {
          // setCsvDialogOpen(false);
          fetchUsers(); // Refresh user list after import
        }}
      />
      <AssignServiceDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        provider={targetProvider}
        availableServices={availableServiceTypes}
        onAssigned={fetchUsers}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title={t("sp_page.confirm_deletion")}
        content={t("sp_page.confirm_delete_msg")}
        loading={isDeleting}
      />
      <ServiceProviderDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        provider={selectedProvider}
        onUpdate={handleProviderUpdate}
      />
    </Box>
  );
};

export default ServiceProviderPage;
